import { ExpandIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import type { AlertsCenterLayout } from '../../utils/alert/alertsCenterCard';

const LIST_BUTTON_CLASS =
  'text-utility-mist-700 hover:bg-primary_hover outline-focus-ring -my-1.5 flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg p-2 transition duration-100 ease-linear focus-visible:outline-2 focus-visible:outline-offset-2';

/** The list chevron at the sheet header's 44px, with nothing to pull back: the 56px band holds it. */
const SHEET_BUTTON_CLASS =
  'text-utility-mist-700 hover:bg-primary_hover outline-focus-ring flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-lg p-2.5 transition duration-100 ease-linear focus-visible:outline-2 focus-visible:outline-offset-2';

/** Figma "Ai Button", on the composer input's surface like the carousel's cards. */
const CAROUSEL_BUTTON_CLASS =
  'bg-primary border-secondary text-utility-mist-700 hover:bg-primary_hover outline-focus-ring flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full border shadow-sm transition duration-100 ease-linear focus-visible:outline-2 focus-visible:outline-offset-2';

/**
 * The surface's name, in both headers and on the card's landmark. Deliberately
 * countless: the landmark is listed by name in screen-reader navigation, and a
 * count in it would rename the landmark on every poll.
 */
export const ALERTS_CENTER_TITLE = 'Alerts';

interface IAlertsCenterHeaderProps {
  title: string;
  /** Muted count after the title — "(5)", "(3 of 5)". Omitted where there is nothing to count. */
  count?: string;
  /** The chevron glyph — points where the card is about to go. */
  icon: React.ComponentType<{ className?: string }>;
  /** Accessible name for the chevron button. */
  collapseLabel: string;
  onCollapse: () => void;
  /** The chevron button, for a host that moves focus onto it. */
  collapseButtonRef?: React.Ref<HTMLButtonElement>;
  /**
   * `carousel` draws a 40px band with round buttons and, when `onViewAll` is
   * set, an expand button before the chevron. @default 'list'
   */
  layout?: AlertsCenterLayout;
  /** The carousel's expand button was clicked. Ignored on `list`. */
  onViewAll?: () => void;
  /**
   * The full center as the phone sheet's own surface: the sheet header's 56px
   * row, a 20px title and a 44px chevron, so the control lands where the
   * assistant's own chevron sat (Figma `9155:239808`).
   */
  fillsSheet?: boolean;
  className?: string;
}

/**
 * The card's header: the title, an optional muted count, and the chevron that
 * collapses the card. Pinned
 * — it sits outside the list's scroll container, so the collapse affordance
 * stays reachable however far the user has scrolled, and it renders in every
 * state including the error one.
 *
 * On `list` the band is the title's line box, not the chevron's: the button
 * keeps its 36px hit area and pulls its overflow back with a negative margin,
 * so the header measures what the design draws. On `carousel` the 40px
 * buttons are the band, as the Figma draws them.
 */
const AlertsCenterHeader = ({
  title,
  count,
  icon: Icon,
  collapseLabel,
  onCollapse,
  collapseButtonRef,
  layout = 'list',
  onViewAll,
  fillsSheet = false,
  className,
}: IAlertsCenterHeaderProps) => {
  const isCarousel = layout === 'carousel';
  const collapseButtonClass = isCarousel ? CAROUSEL_BUTTON_CLASS : fillsSheet ? SHEET_BUTTON_CLASS : LIST_BUTTON_CLASS;

  return (
    <div className={cx('flex shrink-0 items-center justify-between gap-2 px-4', fillsSheet && 'h-14', className)}>
      {/* The leading is set here, not on the title: the wrapper's own strut
          would otherwise hold the band open at the ambient line height. */}
      <div className="min-w-0 flex-1 truncate leading-6">
        {/* `text-lg` carries its own 28px leading, so the band needs this too. */}
        <span className={cx('text-primary font-medium', fillsSheet ? 'text-xl' : 'text-lg leading-6')}>{title}</span>
        {count && <span className="text-tertiary text-md ml-2 font-medium">{count}</span>}
      </div>
      {/* One wrapper in both layouts keeps the chevron at one tree position, so
          it stays mounted, and focused, as the carousel gives way to the full center. */}
      <div className="flex shrink-0 items-center gap-3">
        {isCarousel && onViewAll && (
          <button type="button" aria-label="View all alerts" onClick={onViewAll} className={CAROUSEL_BUTTON_CLASS}>
            <ExpandIcon aria-hidden="true" className="size-5 shrink-0" />
          </button>
        )}
        <button
          ref={collapseButtonRef}
          type="button"
          aria-label={collapseLabel}
          aria-expanded="true"
          onClick={onCollapse}
          className={collapseButtonClass}
        >
          <Icon aria-hidden="true" className={cx('shrink-0', fillsSheet ? 'size-6' : 'size-5')} />
        </button>
      </div>
    </div>
  );
};

export default AlertsCenterHeader;
